// ===== CENTRALIZED API CONFIGURATION =====

const BASE_URL = 'https://bisauli.com';
const API_PATH = '/api3';

export const API_BASE_URL = 'https://bisauli.com/backend/api3';

export const API_CONFIG = {
  // Base URLs
  BASE_URL,
  API_PATH,
  API_BASE_URL: `${BASE_URL}${API_PATH}/controllers`,
  IMAGE_BASE_URL: `${BASE_URL}${API_PATH}`,
  
  // Upload URLs
  UPLOADS: {
    BANNERS: `${BASE_URL}${API_PATH}/uploads/banners`,
    EXPERTS: `${BASE_URL}${API_PATH}/uploads/experts`,
    PROPERTIES: `${BASE_URL}${API_PATH}/uploads/properties`,
    SERVICES: `${BASE_URL}${API_PATH}/uploads/services`,
  },

  // Controller Endpoints
  CONTROLLERS: {
    BANNER: 'BannerController.php',
    AUTH: 'AuthController.php',
    PROPERTY: 'PropertyController.php',
    SERVICE: 'ServiceController.php',
    EXPERT: 'ExpertController.php',
  },

  // Banner Actions
  BANNER_ACTIONS: {
    GET_ALL: 'getBanners',
    CREATE: 'createBanner',
    UPLOAD: 'uploadBanner',
    UPDATE: 'updateBanner',
    DELETE: 'deleteBanner',
  },

  // Expert Actions
  EXPERT_ACTIONS: {
    GET_ALL: 'getExperts',
    GET_CATEGORIES: 'getCategories',
    GET_BY_ID: 'getExpertById',
    CREATE: 'createExpert',
    GET_PENDING: 'getPendingExperts',
    APPROVE: 'approveExpert',
    REJECT: 'rejectExpert',
    DELETE: 'deleteExpert',
  },

  // Property Actions
  PROPERTY_ACTIONS: {
    GET_ALL: 'getProperties',
    CREATE: 'createProperty',
    UPLOAD: 'uploadProperty',
    GET_PENDING: 'getPending',
    APPROVE: 'approveProperty',
    REJECT: 'rejectProperty',
    DELETE: 'deleteProperty',
  },

  // Service Actions
  SERVICE_ACTIONS: {
    GET_ALL: 'getExperts',
    CREATE: 'createService',
    GET_CATEGORIES: 'getCategories',
    GET_BY_ID: 'getById',
    GET_PENDING: 'getPendingServices',
    APPROVE: 'approveService',
    REJECT: 'rejectService',
  },

  // Auth Actions
  AUTH_ACTIONS: {
    LOGIN: 'login',
    REGISTER: 'register',
    VERIFY: 'verify',
    LOGOUT: 'logout',
  },

  // HTTP Methods
  METHODS: {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
  },

  // Default Headers
  HEADERS: {
    JSON: {
      'Content-Type': 'application/json',
    },
    FORM: {
      // FormData automatically sets Content-Type with boundary
    },
  },
};

// ===== URL BUILDER HELPER =====
/**
 * Build API URL with controller, action, and optional parameters
 * @param {string} controller - Controller name from API_CONFIG.CONTROLLERS
 * @param {string} action - Action name from respective ACTION config
 * @param {object} params - Query parameters as key-value pairs
 * @returns {string} Complete API URL
 */
export const buildUrl = (controller, action, params = {}) => {
  let url = `${API_CONFIG.API_BASE_URL}/${controller}?action=${action}`;
  
  // Add query parameters
  Object.keys(params).forEach((key) => {
    if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
      url += `&${key}=${encodeURIComponent(params[key])}`;
    }
  });
  
  return url;
};

// ===== IMAGE URL BUILDER =====
/**
 * Build full image URL from API response path
 * @param {string} imagePath - Image path from API (e.g., "uploads/experts/file.jpg" or "/uploads/...")
 * @param {string} type - Image type (banners, experts, properties, services)
 * @returns {string|null} Complete image URL or null if no path
 */
export const buildImageUrl = (imagePath, type = 'experts') => {
  if (!imagePath || typeof imagePath !== 'string') {
    console.warn('⚠️ Invalid image path:', imagePath);
    return null;
  }

  const trimmedPath = imagePath.trim();

  // If already a full URL
  if (trimmedPath.startsWith('http://') || trimmedPath.startsWith('https://')) {
    return trimmedPath;
  }

  // Remove leading slash if present
  const cleanPath = trimmedPath.startsWith('/') ? trimmedPath.substring(1) : trimmedPath;

  // If relative path with uploads/
  if (cleanPath.startsWith('uploads/')) {
    return `${API_CONFIG.IMAGE_BASE_URL}/${cleanPath}`;
  }

  // Extract filename
  const filename = cleanPath.split('/').pop();
  
  // Use type-specific upload URL
  const uploadType = type.toUpperCase();
  if (API_CONFIG.UPLOADS[uploadType]) {
    return `${API_CONFIG.UPLOADS[uploadType]}/${filename}`;
  }

  // Fallback
  console.warn('⚠️ Unknown upload type:', type);
  return `${API_CONFIG.IMAGE_BASE_URL}/uploads/${uploadType.toLowerCase()}/${filename}`;
};

// ===== HEADERS BUILDER =====
/**
 * Build headers with optional authentication token
 * @param {string} token - Optional Bearer token
 * @param {string} contentType - Content type (json or form)
 * @returns {object} Headers object
 */
export const buildHeaders = (token = null, contentType = 'json') => {
  const headers = { ...API_CONFIG.HEADERS[contentType] };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// ===== ERROR MESSAGE FORMATTER =====
/**
 * Format error response for user-friendly display
 * @param {object} error - Error object from API or fetch
 * @returns {string} Formatted error message
 */
export const formatErrorMessage = (error) => {
  if (typeof error === 'string') {
    return error;
  }

  if (error?.message) {
    return error.message;
  }

  if (error?.data?.message) {
    return error.data.message;
  }

  return 'An unexpected error occurred. Please try again.';
};

// ===== SUCCESS MESSAGE FORMATTER =====
/**
 * Format success response message
 * @param {object} response - API response object
 * @returns {string} Formatted success message
 */
export const formatSuccessMessage = (response) => {
  if (typeof response === 'string') {
    return response;
  }

  return response?.message || 'Operation completed successfully';
};

// ===== LOCAL STORAGE HELPERS =====
export const StorageKeys = {
  AUTH_TOKEN: 'authToken',
  ADMIN_USER: 'adminUser',
  USER_PREFERENCES: 'userPreferences',
};

export const StorageHelpers = {
  setToken: (token) => localStorage.setItem(StorageKeys.AUTH_TOKEN, token),
  getToken: () => localStorage.getItem(StorageKeys.AUTH_TOKEN),
  removeToken: () => localStorage.removeItem(StorageKeys.AUTH_TOKEN),

  setAdminUser: (user) => localStorage.setItem(StorageKeys.ADMIN_USER, JSON.stringify(user)),
  getAdminUser: () => {
    try {
      return JSON.parse(localStorage.getItem(StorageKeys.ADMIN_USER));
    } catch {
      return null;
    }
  },
  removeAdminUser: () => localStorage.removeItem(StorageKeys.ADMIN_USER),

  clearAll: () => localStorage.clear(),
};

// ===== QUERY PARAMETER BUILDER =====
/**
 * Build query string from object
 * @param {object} params - Parameters object
 * @returns {string} Query string
 */
export const buildQueryString = (params = {}) => {
  const filtered = Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  return filtered ? `?${filtered}` : '';
};